import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import Icon from '../Icon/Icon'

import media from '../../../services/generateMediaQueries'

let INPUT_VARIANT = ''

function normalizeDropdownValue(multiple, initialValue) {
  if (!multiple) {
    return initialValue ?? ''
  }
  if (Array.isArray(initialValue)) {
    return initialValue
  }
  if (initialValue === '' || initialValue == null) {
    return []
  }
  return [String(initialValue)]
}

const Placeholder = styled.span`
  display: flex;
  align-items: center;
  padding: 0 0.25rem;
  color: var(--color-fg-light) ;
  position: absolute;
  top: 0.5rem;
  height: 3rem;
  left: 0.75rem;
  transition-property: font-size, height, background-color, padding, top;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-easing);
  &:empty {
    display: none;
  }

  ${props =>
    props.focused &&
    css`
      color: var(--color-primary) !important;
    `}
  
  ${props =>
    props.hasError &&
    (props.hasValue || props.focused) &&
    css`
      color: var(--color-error);
    `}
  
  ${props => {
    if (props.hasValue || props.focused) {
      if (props.floating) {
        return css`
          background-color: var(--color-bg-form);
          height: 1rem;
          padding: 0 0.25rem;
          font-size: 0.75em;
          top: 0;
          left: 0.75rem;
        `
      }
      return css`
        display: none;
      `
    }

    return null
  }}

  ${() =>
    INPUT_VARIANT === 'white' &&
    css`
      position: unset;
      z-index: 1;
      font-size: 1rem;
      padding: 1.25rem 1rem;
      background: transparent;

      ${media.lessThan('small')`
        display: none;
      `}
    `}
    ${({ variant }) =>
      variant === 'small'
        ? css`
            font-size: 0.8rem;
          `
        : css`
            font-size: 1rem;
          `}

      
    ${({ variant, hasValue }) =>
      variant === 'small' &&
      !hasValue &&
      css`
        color: var(--color-fg);
      `}
`

const Control = styled.select`
  appearance: none;
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0 1rem;
  font-weight: var(--font-weight);

  margin-top: 1px;

  outline: 0;
  border: 0;
  background-color: transparent;

  ${props =>
    props.$multiple &&
    css`
      min-height: 10rem;
      padding: 0.75rem 1rem;
      height: auto;
    `}

  :disabled {
    opacity: 0.5;
  }

  :-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 #000000;
  }

  & option:not(:checked) {
    color: currentColor;
  }

  ${() =>
    INPUT_VARIANT === 'white' &&
    css`
      padding-left: 0;

      ${media.lessThan('small')`
        padding-left: 1rem;
      `}
    `}
`

const Hint = styled.span`
  // z-index: 2;
  position: absolute;
  bottom: 0;
  right: 0;
  width: 3rem;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ variant }) =>
    variant === 'small' &&
    css`
      height: 3.5rem;
      span {
        font-size: 0.9rem;
      }
    `}
`

const Border = styled.span`
  position: absolute;
  background-color: ${({ backgroundColor }) =>
    backgroundColor || 'transparent'};
  border-radius: var(--border-radius-form);
  border: var(--border-form);
  transition: border-color var(--animation-duration) var(--animation-easing);
  width: 100%;
  height: 3rem;

  ${props =>
    props.$multiple &&
    css`
      height: 100%;
      min-height: 10rem;
    `}
  
  ${props =>
    props.focused &&
    css`
      border-color: var(--color-primary);
      transition: all var(--animation-duration) var(--animation-easing);
    `}
  
  ${props =>
    props.hasError &&
    css`
      border-color: var(--color-error);
    `}

  ${() =>
    INPUT_VARIANT === 'white' &&
    css`
      background-color: white;
      border: none;
      border-radius: var(--border-radius-form);
      box-shadow: 0px 8px 10px 0px rgba(0, 0, 0, 0.05);
    `}
`

const ControlBase = styled.label`
  position: relative;
  display: block;
  width: 100%;
  height: 3.5rem;
  padding-top: 0.5rem;
  font-size: 1rem;

  ${props =>
    props.$multiple &&
    css`
      height: auto;
      min-height: 10.5rem;
    `}

  ${() =>
    INPUT_VARIANT === 'white' &&
    css`
      display: flex;
    `}
`

const ComponentBase = styled.div`
  margin-bottom: 0.625rem;
`

const ErrorMessage = styled.output`
  display: block;
  color: var(--color-error);
  padding: 0 1rem;
  font-size: 0.75em;
  margin-top: 0.25rem;
  :empty {
    display: none;
  }
`
const DropdownLabel = styled.span`
  display: flex;
  align-items: center;
  padding: 0 0.25rem;
  color: #fff;
  position: absolute;
  top: 0.5rem;
  height: 3rem;
  left: 0.75rem;
  &:empty {
    display: none;
  }

  ${() =>
    INPUT_VARIANT === 'white' &&
    css`
      position: unset;
      z-index: 1;
      font-size: 1rem;
      padding: 1.25rem 1rem;
      background: transparent;

      ${media.lessThan('small')`
      display: none;
    `}
    `}
`
class Dropdown extends React.Component {
  constructor(props) {
    super(props)
    const { initialValue, multiple } = props
    this.state = {
      value: normalizeDropdownValue(multiple, initialValue),
      focused: false,
    }
    this.input = React.createRef()
  }

  componentDidMount() {
    this.syncSelectDom()
  }

  componentDidUpdate(prevProps) {
    const { initialValue, multiple } = this.props

    if (
      prevProps.initialValue !== initialValue ||
      prevProps.multiple !== multiple
    ) {
      this.setState(
        {
          value: normalizeDropdownValue(multiple, initialValue),
        },
        () => this.syncSelectDom(),
      )
      return
    }

    this.syncSelectDom()
  }

  syncSelectDom = () => {
    const el = this.input.current
    if (!el) return

    const { multiple } = this.props
    const { value } = this.state

    if (multiple) {
      const selected = Array.isArray(value) ? value : []
      Array.from(el.options).forEach(opt => {
        opt.selected = selected.includes(opt.value)
      })
      return
    }

    el.value = value
  }

  handleChange = e => {
    const { multiple, onChange } = this.props

    if (multiple) {
      const selected = Array.from(e.target.selectedOptions).map(o => o.value)
      this.setState({
        value: selected,
      })
      if (onChange) {
        onChange(e)
      }
      return
    }

    this.setState({
      value: e.target.value,
    })
    if (onChange) {
      onChange(e)
    }
  }

  handleFocus = e => {
    this.setState({
      focused: true,
    })
    const { onFocus } = this.props
    if (onFocus) {
      onFocus(e)
    }
  }

  handleBlur = e => {
    this.setState({
      focused: false,
    })
    const { onBlur } = this.props
    if (onBlur) {
      onBlur(e)
    }
  }

  renderGroupedOptions = () => {
    const { options, group } = this.props
    const optionGroups = options.reduce((groupedOptions, option) => {
      const theGroupedOptions = groupedOptions
      const optionGroup = group(option)
      if (typeof theGroupedOptions[optionGroup] === 'undefined') {
        theGroupedOptions[optionGroup] = []
      }

      theGroupedOptions[optionGroup].push(option)
      return theGroupedOptions
    }, {})

    return Object.keys(optionGroups).map(optionGroup => (
      <optgroup key={optionGroup} label={optionGroup}>
        {optionGroups[optionGroup].map(this.renderSingleOption)}
      </optgroup>
    ))
  }

  renderSingleOption = option => {
    let theOption
    switch (typeof option) {
      case 'string':
        theOption = {
          name: option,
          value: option,
        }
        break
      default:
      case 'object':
        theOption = option
        break
    }

    const { value, name } = theOption

    return (
      <option key={`${name}:${value}`} value={value}>
        {name}
      </option>
    )
  }

  renderOptions = () => {
    const { options, group } = this.props

    if (typeof group === 'function') {
      return this.renderGroupedOptions()
    }

    return options.map(this.renderSingleOption)
  }

  render() {
    const {
      placeholder,
      className,
      touched,
      error,
      floatingLabel,
      backgroundColor,
      variant,
      label,
      iconColor,
      multiple,
      ...props
    } = this.props
    const { value, focused } = this.state

    const hasValue = multiple
      ? Array.isArray(value) && value.length > 0
      : value !== ''

    INPUT_VARIANT = variant

    return (
      <ComponentBase className={className}>
        <ControlBase $multiple={multiple}>
          <Border
            $multiple={multiple}
            focused={focused}
            hasValue={hasValue}
            hasError={touched && error}
            backgroundColor={backgroundColor}
          />
          <Hint onClick={() => {}} variant={INPUT_VARIANT}>
            <Icon name="caret" style={iconColor ? { color: iconColor } : {}} />
          </Hint>
          {label && <DropdownLabel>{label}</DropdownLabel>}
          <Placeholder
            focused={focused}
            hasValue={hasValue}
            hasError={touched && error}
            floating={floatingLabel}
            variant={INPUT_VARIANT}
          >
            {placeholder}
          </Placeholder>
          <Control
            {...props}
            multiple={multiple}
            ref={this.input}
            $multiple={multiple}
            focused={focused}
            hasError={error}
            onChange={this.handleChange}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
          >
            {!this.props.required && !multiple && <option value="" />}
            {this.renderOptions()}
          </Control>
        </ControlBase>
        <ErrorMessage>{touched && error}</ErrorMessage>
      </ComponentBase>
    )
  }
}

Dropdown.defaultProps = {
  floatingLabel: true,
  placeholder: '',
  initialValue: '',
  initialError: null,
  options: [],
  multiple: false,
}

Dropdown.propTypes = {
  floatingLabel: PropTypes.bool,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  initialValue: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  initialError: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        value: PropTypes.any,
        name: PropTypes.string,
      }),
      PropTypes.string,
    ]),
  ),
  group: PropTypes.func,
}

export default Dropdown
